import { RefObject } from 'react'
import { $thm } from '~/common/themeSx'
import { asLink, Box, BoxProps, Flex, FlexProps, forwardRef, ScrollShadowBox, Stack, StackProps } from '~/design_system'

/**
 * @module ItemsList
 *
 * This module contains `ItemsList` and related components. The purpose of
 * these components is to provide an easy way to render a list of items, either
 * as buttons (which react to `onClick`) or links (which take `href`).
 *
 * @see `ItemsList.stories.jsx` for usage examples.
 */

/**
 * The main container for a list of items. Use the other components in this
 * module as direct children of `ItemsList`.
 *
 * The underlying component is Chakra's `Stack`.
 *
 * Optionally pass your own component width breakpoints to modify when the ResultItem layout changes occur.
 */
export const ItemsList = forwardRef<StackProps, 'div'>(({ sx, ...props }, ref) => {
  return (
    <Stack
      data-items-list
      data-card
      ref={ref as RefObject<HTMLDivElement | null>}
      spacing={0}
      flex="1"
      layerStyle="card"
      sx={$thm({
        p: 6,
        '*:is([data-card], [data-card-body]) &': {
          p: 0,
          borderRadius: { $base: 'inherit', $canvas: 'lg' }
        },
        '[data-results-layout-main-content] &': {
          p: 0,
          '&:not([data-items-list-display-mode="grid"])': { p: { $canvas: 2 } },
          // Full-page result lists scroll with the page, not in ItemsListItems' scroll region, whose overflow and
          // inherited card radius would clip the rows' rounded hover corners.
          '&[data-result-items]:not([data-items-list-display-mode="grid"]) [data-items-list-items]': {
            overflow: { $canvas: 'visible' }
          },
          borderTopRadius: {
            $base: 'inherit',
            $canvas: 'var(--vndly-items-list-start-radius, var(--chakra-radii-xxl))'
          },
          borderBottomRadius: {
            $base: 'inherit',
            $canvas: 'var(--vndly-items-list-end-radius, var(--chakra-radii-xxl))'
          }
        },
        '[data-card] > [data-card-body]:first-child > &': {
          borderRadius: { $base: 'inherit' },
          mt: { $canvas: -3 },
          borderTopRadius: { $canvas: 'lg' }
        },
        '[data-card] > [data-card-body]:last-child > &': {
          borderRadius: { $base: 'inherit' },
          mb: { $canvas: -3 },
          borderBottomRadius: { $canvas: 'lg' }
        },
        '[data-sidebar-list-view] &': {
          borderTop: { $canvas: 'none' },
          borderBottom: { $canvas: 'none' },
          borderLeft: { $canvas: 'none' },
          borderRight: { $canvas: 'none' },
          boxShadow: { $canvas: 'none' },
          borderRadius: { $canvas: 0 },
          bg: { $canvas: 'transparent' }
        },
        ...sx
      })}
      {...props}
    />
  )
})

ItemsList.displayName = 'ItemsList'

/**
 * The header for the list of items.
 *
 * The underlying component is Chakra's `Flex`.
 */
export const ItemsListHeader = forwardRef<FlexProps, 'div'>(({ sx, ...props }: FlexProps, ref) => {
  return (
    <Flex
      ref={ref}
      data-items-list-header
      as="header"
      align="center"
      borderColor="var(--vndly-color-container-border)"
      borderTopRadius="inherit"
      sx={$thm({
        mb: { $base: 0, $canvas: 6 },
        px: { $base: 6, $canvas: 3 },
        py: 3,
        borderBottomWidth: { $base: '1px', $canvas: 0 },
        '[data-items-list] &': {
          py: { $canvas: 0 }
        },
        '[data-sidebar-list-view] &': {
          pt: { $canvas: 3 }
        },
        '*:is([data-card], [data-card-body]) > [data-items-list] &': {
          px: { $canvas: 3 }
        },
        ...sx
      })}
      {...props}
    />
  )
})

/**
 * The footer for the list of items.
 *
 * The underlying component is Chakra's `Flex`.
 */
export const ItemsListFooter = forwardRef<FlexProps, 'div'>(({ sx, ...props }: FlexProps, ref) => {
  return (
    <Flex
      ref={ref}
      data-items-list-footer
      as="footer"
      align="center"
      color="var(--vndly-color-container-subdued)"
      py={3}
      borderColor="var(--vndly-color-container-border)"
      sx={{
        ...$thm({
          px: { $base: 6, $canvas: 3 },
          '*:is([data-card], [data-card-body]) &': {
            mt: 6,
            ml: { $base: '1px', $canvas: 0 },
            px: { $canvas: 0 },
            borderBottomRightRadius: 'inherit'
          }
        }),
        ...sx
      }}
      {...props}
    />
  )
})

/**
 * The container for the list of items.
 *
 * If you'd like your items to be displayed in a collapsible format rather than
 * as links or buttons, use Chakra's `Accordion` component instead.
 *
 * The underlying component is Chakra's `Box`.
 */
export const ItemsListItems = forwardRef<BoxProps, 'div'>(({ sx, ...props }: BoxProps, ref) => (
  <ScrollShadowBox
    data-items-list-items
    data-testid="items-list-items"
    role="list"
    ref={ref}
    borderRadius="inherit"
    sx={{
      ...$thm({
        '[data-items-list-header] ~ &, [data-sidebar-list-header] ~ &': {
          borderTopRadius: 0
        },
        '&:has(+ [data-items-list-footer]), &:has(+ [data-split-list-footer])': {
          borderBottomRadius: { $canvas: 0 }
        }
      }),
      ...sx
    }}
    {...props}
  />
))

ItemsListItems.displayName = 'ItemsListItems'

// these styles have been copied from `Accordion` and `AccordionItem` so we can
// share similar looks between views. in `@chakra-ui/react@^1.0.0`, we'll be
// able to use `apply="Accordion.root"` and `apply="Accordion.Item"` to place
// those styles on this component instead

/**
 * A list item that displays information with no attached interactivity. If the
 * user should be able to interact with this item, nest the `ItemsListButton` or
 * `ItemsListLink` within the `ItemsListItem`.
 *
 * The underlying component is Chakra's `Box`.
 */
export const ItemsListItem = forwardRef<BoxProps, 'div'>(({ sx, ...props }: BoxProps, ref) => {
  return (
    <Box
      data-testid="items-list-item"
      data-items-list-item
      role="listitem"
      ref={ref}
      display="flex"
      position="relative"
      alignItems="center"
      w="full"
      p={6}
      borderColor="var(--vndly-color-container-border)"
      borderTopWidth="1px"
      outline="0"
      _focus={{ boxShadow: 'outline' }}
      bg="var(--vndly-color-container-light)"
      transitionDuration=".1s"
      transitionProperty="background"
      sx={$thm({
        px: { $base: 6, $canvas: 3 },
        _first: {
          borderTopWidth: 0,
          borderTopRadius: 'var(--vndly-items-list-start-radius, inherit)'
        },
        _last: {
          borderBottomWidth: 0,
          borderBottomRadius: 'var(--vndly-items-list-end-radius, inherit)'
        },
        '*:is([data-card], [data-card-body]) > [data-items-list] &': {
          px: { $canvas: 3 }
        },
        ...sx
      })}
      {...props}
    />
  )
})

ItemsListItem.displayName = 'ItemsListItem'

/**
 * A list item, represented as a button.
 *
 * Provide `onClick` to each item to manage when it is clicked.
 *
 * The underlying component is Chakra's `Box`.
 */
export const ItemsListButton = forwardRef<BoxProps, 'div'>(({ sx, ...props }, ref) => {
  return (
    <Box
      data-items-list-button
      as="button"
      type="button"
      ref={ref}
      flexGrow={1}
      display="flex"
      alignItems="center"
      textAlign="left"
      m={-6}
      p={6}
      _hover={{ bg: 'blackAlpha.50' }}
      sx={{
        ...$thm({
          '*:is([data-card], [data-card-body]) > [data-items-list] &': {
            px: { $canvas: 3 }
          }
        }),
        ...sx
      }}
      {...props}
    />
  )
})

ItemsListButton.displayName = 'ItemsListButton'

/**
 * A list item, represented as a link.
 *
 * Provide `href` to control the link.
 *
 * The underlying component is Chakra's `Box`.
 */
export const ItemsListLink = forwardRef<BoxProps & { href: string }, 'div'>(({ sx, href, ...props }, ref) => {
  return (
    <Box
      data-items-list-link
      {...asLink(href)}
      variant="unstyled"
      ref={ref}
      flexGrow={1}
      display="flex"
      alignItems="center"
      m={-6}
      p={6}
      _hover={{ textDecoration: 'none', color: 'inherit', bg: 'blackAlpha.50' }}
      sx={{
        ...$thm({
          '*:is([data-card], [data-card-body]) > [data-items-list] &': {
            px: { $canvas: 3 }
          }
        }),
        ...sx
      }}
      {...props}
    />
  )
})

ItemsListLink.displayName = 'ItemsListLink'
